import React from 'react';
import { Box, Typography, Avatar, Chip, Stack } from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import { ChatBubbleOutline, PushPin, Lock, ArrowUpward } from '@mui/icons-material';
import { Link } from 'react-router-dom';

export const timeAgoHu = (isoString) => {
  try {
    if (!isoString) return '';
    const diff = Math.floor((Date.now() - new Date(isoString).getTime()) / 1000);
    if (diff < 60) return 'most';
    if (diff < 3600) return `${Math.floor(diff / 60)} perce`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} órája`;
    if (diff < 7 * 86400) return `${Math.floor(diff / 86400)} napja`;
    if (diff < 30 * 86400) return `${Math.floor(diff / (7 * 86400))} hete`;
    if (diff < 365 * 86400) return `${Math.floor(diff / (30 * 86400))} hónapja`;
    return `${Math.floor(diff / (365 * 86400))} éve`;
  } catch { return ''; }
};

const authorInitials = (thread) => {
  if (thread.isAnonymous) return '?';
  return ((thread.author?.firstName?.[0] ?? '') + (thread.author?.lastName?.[0] ?? '')).toUpperCase() || '?';
};

const authorName = (thread) => {
  if (thread.isAnonymous) return 'Névtelen felhasználó';
  return `${thread.author?.firstName ?? ''} ${thread.author?.lastName ?? ''}`.trim();
};

/**
 * Egységes témakártya a fórum főoldalához és a kategórianézethez.
 * - showCategory: a kategória címke megjelenítése (kategórián belül felesleges)
 * - showScore: pontszám (upvote) megjelenítése
 * - showTags: címkék megjelenítése
 * - activity: ha igaz, az utolsó hozzászólás idejét mutatja a létrehozás helyett
 */
const ThreadListItem = ({ thread, showCategory = true, showScore = false, showTags = false, activity = false }) => {
  const theme = useTheme();
  const hasReply = !!thread.lastReplyAt;
  const time = activity ? (thread.lastReplyAt ?? thread.createdAt) : thread.createdAt;
  const timeLabel = activity ? (hasReply ? 'utolsó válasz' : 'létrehozva') : '';
  const tags = showTags ? (thread.tags ?? []) : [];

  return (
    <Box
      component={Link}
      to={`/forum/tema/${thread.id}`}
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: { xs: 1.5, sm: 2 },
        p: { xs: 1.75, sm: 2.25 },
        minWidth: 0,
        textDecoration: 'none',
        color: 'inherit',
        bgcolor: 'background.paper',
        borderRadius: '14px',
        border: `1px solid ${alpha(theme.palette.primary.main, thread.isPinned ? 0.28 : 0.1)}`,
        borderLeft: thread.isPinned ? `4px solid ${theme.palette.warning.main}` : undefined,
        boxShadow: `0 1px 6px ${alpha(theme.palette.primary.dark, 0.05)}`,
        transition: 'box-shadow 0.18s ease, border-color 0.18s ease, transform 0.18s ease',
        '&:hover': {
          boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.14)}`,
          borderColor: alpha(theme.palette.primary.main, 0.3),
          transform: { md: 'translateY(-1px)' },
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      }}
    >
      <Avatar sx={{
        width: { xs: 36, sm: 42 },
        height: { xs: 36, sm: 42 },
        flexShrink: 0,
        bgcolor: thread.isAnonymous
          ? alpha(theme.palette.text.secondary, 0.15)
          : alpha(theme.palette.primary.main, 0.12),
        color: thread.isAnonymous ? 'text.secondary' : 'primary.main',
        fontWeight: 700,
        fontSize: '0.85rem',
      }}>
        {authorInitials(thread)}
      </Avatar>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        {(thread.isPinned || thread.isLocked) && (
          <Stack direction="row" spacing={0.75} sx={{ mb: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
            {thread.isPinned && (
              <Chip
                icon={<PushPin sx={{ fontSize: 13 }} />}
                label="Kitűzött"
                size="small"
                sx={{
                  height: 22, fontSize: 11.5, fontWeight: 600,
                  bgcolor: alpha(theme.palette.warning.main, 0.14),
                  color: 'warning.dark',
                  '& .MuiChip-icon': { color: 'warning.dark' },
                }}
              />
            )}
            {thread.isLocked && (
              <Chip
                icon={<Lock sx={{ fontSize: 13 }} />}
                label="Lezárt"
                size="small"
                sx={{ height: 22, fontSize: 11.5, bgcolor: alpha(theme.palette.text.secondary, 0.1), color: 'text.secondary' }}
              />
            )}
          </Stack>
        )}

        <Typography
          component="h3"
          variant="body1"
          sx={{
            fontWeight: 600,
            lineHeight: 1.4,
            color: 'text.primary',
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            overflowWrap: 'anywhere',
            mb: 0.75,
          }}
        >
          {thread.title}
        </Typography>

        {tags.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mb: 0.75 }}>
            {tags.slice(0, 4).map((tag) => (
              <Chip
                key={tag}
                label={`#${tag}`}
                size="small"
                sx={{
                  height: 20, fontSize: 11, maxWidth: '100%',
                  bgcolor: alpha(theme.palette.secondary.main, 0.2),
                  color: 'text.primary',
                }}
              />
            ))}
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1, rowGap: 0.5, flexWrap: 'wrap' }}>
          {showCategory && thread.category?.name && (
            <Box sx={{
              px: 1,
              py: 0.2,
              borderRadius: '6px',
              maxWidth: '100%',
              bgcolor: alpha(theme.palette.primary.main, 0.08),
              border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
            }}>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, fontSize: '0.72rem', display: 'block', overflowWrap: 'anywhere' }}>
                {thread.category.name}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.78rem', overflowWrap: 'anywhere' }}>
            {authorName(thread)}
            {time && ` · ${timeLabel ? `${timeLabel} ` : ''}${timeAgoHu(time)}`}
          </Typography>
        </Box>
      </Box>

      {/* Statisztika: válaszok (és pont) */}
      <Box sx={{
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.5,
        minWidth: 36,
        color: 'text.secondary',
      }}>
        <Box
          aria-label={`${thread.replyCount} válasz`}
          sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5,
            px: 1, py: 0.35, borderRadius: '999px',
            bgcolor: thread.replyCount > 0 ? alpha(theme.palette.primary.main, 0.1) : alpha(theme.palette.text.secondary, 0.08),
            color: thread.replyCount > 0 ? 'primary.main' : 'text.secondary',
          }}
        >
          <ChatBubbleOutline sx={{ fontSize: 15 }} />
          <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1 }}>{thread.replyCount}</Typography>
        </Box>
        {showScore && (
          <Box aria-label={`${thread.score} pont`} sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, pr: 0.5 }}>
            <ArrowUpward sx={{ fontSize: 14 }} />
            <Typography variant="caption" sx={{ fontWeight: 600, lineHeight: 1 }}>{thread.score}</Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ThreadListItem;
